const baseUrl = import.meta.env.VITE_API_URL as string;

if (!baseUrl) {
  throw new Error('VITE_API_URL is required');
}

export interface ApiErrorBody {
  error?: { code?: string };
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, code?: string) {
    super(code ?? `Request failed: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const parseError = async (response: Response): Promise<ApiError> => {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return new ApiError(response.status, body.error?.code);
  } catch {
    return new ApiError(response.status);
  }
};

const buildUrl = (path: string, query?: Record<string, string | number | undefined>): string => {
  if (!query) return `${baseUrl}${path}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${baseUrl}${path}?${qs}` : `${baseUrl}${path}`;
};

export const api = {
  get: async <T>(
    path: string,
    query?: Record<string, string | number | undefined>,
  ): Promise<T> => {
    const response = await fetch(buildUrl(path, query));
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as T;
  },
  post: async <T>(
    path: string,
    body: unknown,
    headers: Record<string, string> = {},
  ): Promise<T> => {
    const response = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trinity-Client': 'give-web/0.1.0',
        ...headers,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as T;
  },
};
